import { pageIdsEqual } from '@/lib/page-id';
import type { PageEntry, ParsedEntryId } from '../types';

/**
 * Read a flattened-or-nested attribute from an API page object.
 *
 * `mapObject` on the API side spreads `entity.attributes` at the top level
 * of the response, so `obj.legacyModuleId` is the canonical shape — but
 * older callers and some background jobs still nest under `attributes`.
 * This helper accepts either, returning `undefined` if neither path has a
 * value of the expected type. Hoisted to module scope so the entries
 * useMemo, the archive flow, and any future consumer all share one
 * implementation (previously two copies had drifted).
 */
export function readPageObjAttr<V = unknown>(obj: any, key: string): V | undefined {
  if (obj == null) return undefined;
  if (obj[key] !== undefined) return obj[key] as V;
  const attrs = obj.attributes;
  if (attrs && typeof attrs === 'object' && attrs[key] !== undefined) {
    return attrs[key] as V;
  }
  return undefined;
}

/** Parse a composite PageEntry id ('m:<id>' | 'o:<id>') into source + rawId. */
export function parseEntryId(compositeId: string): ParsedEntryId {
  if (compositeId.startsWith('o:')) return { source: 'object', rawId: compositeId.slice(2) };
  if (compositeId.startsWith('m:')) return { source: 'module', rawId: compositeId.slice(2) };
  // Legacy callers that pass an unprefixed module id — assume module source.
  return { source: 'module', rawId: compositeId };
}

/**
 * Find the merged-list entry a page id refers to, tolerating every id FORM
 * the streaming pipeline can carry: the entry's own composite id
 * (`o:pg_*` / `m:<raw>`), the canonical `pg_*` object id, the raw
 * html_viewer module id, or — for object-sourced entries — the paired
 * `legacyModuleId`. The stream dispatches canonical `pg_*` ids while module
 * rows key on the raw module id, so a strict composite-id lookup misses the
 * very entry that is mid-build. (#pages-streaming-regression)
 *
 * An exact composite-id match wins over family resolution so sibling
 * entries that escaped dedup (object + module of the same page both listed)
 * can never shadow a precise selection.
 */
export function findEntryByPageIdFamily(
  entries: PageEntry[],
  id: string | null | undefined,
): PageEntry | undefined {
  if (!id) return undefined;
  const exact = entries.find((e) => e.id === id);
  if (exact) return exact;
  return entries.find((e) => {
    if (pageIdsEqual(e.rawId, id)) return true;
    if (e.source === 'object') {
      const legacy = readPageObjAttr<string>(e.raw, 'legacyModuleId');
      if (legacy && pageIdsEqual(legacy, id)) return true;
    }
    return false;
  });
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
